import { useState, useEffect } from "react";
import {
  Phone, Smartphone, CheckCircle2, Clock, Plus, Search,
  AlertCircle, Trash2, Play, Check, X, User, ArrowRight,
  PhoneCall, Send, Sparkles, Filter
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from "@/components/ui/dialog";
import { toast } from "sonner";

const INITIAL_ACTIONS = [
  {
    id: "ma-1",
    contactName: "C & L Construction",
    phone: "(760) 375-4066",
    email: "contact@clconstruction.com",
    type: "call",
    title: "Follow up on $12,500 roof renovation estimate",
    workflow: "AI Speed-to-Lead Followup",
    priority: "High",
    dueDate: "Today, 5:00 PM",
    status: "pending",
    script: "Hi, this is Colibri follow-up regarding your roof estimate inquiry. Do you have 5 minutes to review the warranty and timeline?",
    notes: "Client asked for 10-minute demo call. Inquired about commercial warranty."
  },
  {
    id: "ma-2",
    contactName: "Marcus Vance",
    phone: "(555) 349-8821",
    email: "marcus.vance@techcorp.io",
    type: "sms",
    title: "Send consultation confirmation & intake link",
    workflow: "Discovery Call Booking",
    priority: "Medium",
    dueDate: "Today, 6:30 PM",
    status: "pending",
    script: "Hi Marcus! Looking forward to our call Tuesday at 2:00 PM. Please complete your project scope form here: https://deck.weddingsthyzfoto.com/portal",
    notes: "Booked 2:00 PM Tuesday via public booking calendar."
  },
  {
    id: "ma-3",
    contactName: "Elena Rostova",
    phone: "(555) 782-9011",
    email: "elena@vanguardlaw.com",
    type: "call",
    title: "Pre-onboarding scope verification",
    workflow: "Client Onboarding",
    priority: "Normal",
    dueDate: "Tomorrow, 10:00 AM",
    status: "pending",
    script: "Hello Elena, calling to verify your team size and CRM integration requirements before onboarding setup.",
    notes: "Review custom integrations required for their portal."
  },
  {
    id: "ma-4",
    contactName: "Sophia Rossi",
    phone: "(555) 432-1980",
    email: "sophia@rossidesign.co",
    type: "sms",
    title: "Deliver finalized pricing proposal & retainer link",
    workflow: "Proposal Sent",
    priority: "High",
    dueDate: "Tomorrow, 2:00 PM",
    script: "Hi Sophia, your updated project proposal #PR-882 is ready! You can review and lock in the start date here: https://deck.weddingsthyzfoto.com/pay/retainer",
    status: "pending",
    notes: "Sent proposal #PR-882. Waiting for retainer authorization."
  }
];

export default function ManualActionsTab() {
  const [actions, setActions] = useState(() => {
    const saved = localStorage.getItem("ld_manual_actions");
    if (saved) {
      try { return JSON.parse(saved); } catch {}
    }
    return INITIAL_ACTIONS;
  });

  useEffect(() => {
    localStorage.setItem("ld_manual_actions", JSON.stringify(actions));
  }, [actions]);

  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("pending");
  const [searchQuery, setSearchQuery] = useState("");

  // Modals
  const [executingAction, setExecutingAction] = useState(null);
  const [callTimer, setCallTimer] = useState(0);
  const [callConnected, setCallConnected] = useState(false);
  const [callNotes, setCallNotes] = useState("");
  const [smsDraft, setSmsDraft] = useState("");
  const [isAddOpen, setIsAddOpen] = useState(false);

  // New action form state
  const [newAction, setNewAction] = useState({
    contactName: "",
    phone: "",
    email: "",
    type: "call",
    title: "",
    workflow: "Direct Agent Task",
    priority: "High",
    dueDate: "Today, 5:00 PM",
    script: "",
    notes: ""
  });

  // Call timer effect
  useEffect(() => {
    let interval = null;
    if (executingAction && executingAction.type === "call" && callConnected) {
      interval = setInterval(() => {
        setCallTimer(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [executingAction, callConnected]);

  const handleStartExecute = (action) => {
    setExecutingAction(action);
    setCallTimer(0);
    setCallConnected(false);
    setCallNotes(action.notes || "");
    setSmsDraft(action.script || "");

    if (action.type === "call") {
      setTimeout(() => {
        setCallConnected(true);
      }, 1200);
    }
  };

  const handleCompleteExecution = () => {
    if (!executingAction) return;
    setActions(prev =>
      prev.map(a =>
        a.id === executingAction.id
          ? { ...a, status: "completed", completedAt: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }), notes: callNotes || a.notes }
          : a
      )
    );
    toast.success(
      executingAction.type === "call"
        ? `Call to ${executingAction.contactName} logged and marked complete!`
        : `SMS dispatched to ${executingAction.contactName} and task completed!`
    );
    setExecutingAction(null);
  };

  const handleQuickComplete = (id) => {
    setActions(prev =>
      prev.map(a =>
        a.id === id
          ? { ...a, status: "completed", completedAt: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) }
          : a
      )
    );
    toast.success("Action marked as completed!");
  };

  const handleDeleteAction = (id) => {
    setActions(prev => prev.filter(a => a.id !== id));
    toast.success("Task deleted");
  };

  const handleCreateAction = (e) => {
    e.preventDefault();
    if (!newAction.contactName.trim() || !newAction.title.trim()) {
      toast.error("Please enter contact name and task title");
      return;
    }
    const created = {
      id: "ma-" + Date.now(),
      ...newAction,
      status: "pending"
    };
    setActions(prev => [created, ...prev]);
    toast.success("New manual action added to queue!");
    setIsAddOpen(false);
    setNewAction({
      contactName: "",
      phone: "",
      email: "",
      type: "call",
      title: "",
      workflow: "Direct Agent Task",
      priority: "High",
      dueDate: "Today, 5:00 PM",
      script: "",
      notes: ""
    });
  };

  const filtered = actions.filter(a => {
    if (filterStatus === "pending" && a.status !== "pending") return false;
    if (filterStatus === "completed" && a.status !== "completed") return false;
    if (filterType !== "all" && a.type !== filterType) return false;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      return (
        a.contactName.toLowerCase().includes(q) ||
        a.title.toLowerCase().includes(q) ||
        a.phone.includes(q)
      );
    }
    return true;
  });

  const pendingCount = actions.filter(a => a.status === "pending").length;
  const completedCount = actions.filter(a => a.status === "completed").length;
  const callsCount = actions.filter(a => a.status === "pending" && a.type === "call").length;
  const smsCount = actions.filter(a => a.status === "pending" && a.type === "sms").length;

  const formatSeconds = (sec) => {
    const mins = Math.floor(sec / 60);
    const s = sec % 60;
    return `${mins.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  };

  return (
    <div className="flex-1 h-full overflow-y-auto bg-slate-50 dark:bg-slate-950 p-6 font-sans">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <CheckCircle2 className="w-6 h-6 text-blue-600" />
              Manual Actions Queue
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Active queue for phone calls, outbound SMS touches, and agent reviews triggered by workflows or CRM events.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => setIsAddOpen(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white gap-2 shadow-xs"
            >
              <Plus className="w-4 h-4" />
              + Add Manual Action
            </Button>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card className="p-4 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Pending Actions</div>
            <div className="text-2xl font-bold text-slate-900 dark:text-slate-100 mt-1">{pendingCount}</div>
            <div className="text-xs text-blue-600 mt-1 font-medium">{callsCount} Calls · {smsCount} SMS</div>
          </Card>
          <Card className="p-4 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Completed Today</div>
            <div className="text-2xl font-bold text-emerald-600 mt-1">{completedCount}</div>
            <div className="text-xs text-slate-400 mt-1">Average execution: 1.8 min</div>
          </Card>
          <Card className="p-4 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Overdue Tasks</div>
            <div className="text-2xl font-bold text-slate-900 dark:text-slate-100 mt-1">0</div>
            <div className="text-xs text-emerald-500 mt-1 font-medium">100% on schedule</div>
          </Card>
          <Card className="p-4 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">SLA Fulfillment</div>
            <div className="text-2xl font-bold text-blue-600 mt-1">98.4%</div>
            <div className="text-xs text-slate-400 mt-1">Target: &gt; 95%</div>
          </Card>
        </div>

        {/* Filters and Search Bar */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 bg-white dark:bg-slate-900 p-3 rounded-lg border border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-2 overflow-x-auto pb-1 sm:pb-0">
            <button
              onClick={() => { setFilterStatus("pending"); setFilterType("all"); }}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                filterStatus === "pending" && filterType === "all"
                  ? "bg-blue-600 text-white shadow-xs"
                  : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200"
              }`}
            >
              All Pending ({pendingCount})
            </button>
            <button
              onClick={() => { setFilterStatus("pending"); setFilterType("call"); }}
              className={`px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 transition-colors ${
                filterStatus === "pending" && filterType === "call"
                  ? "bg-blue-600 text-white shadow-xs"
                  : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200"
              }`}
            >
              <Phone className="w-3.5 h-3.5" />
              Calls ({callsCount})
            </button>
            <button
              onClick={() => { setFilterStatus("pending"); setFilterType("sms"); }}
              className={`px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 transition-colors ${
                filterStatus === "pending" && filterType === "sms"
                  ? "bg-blue-600 text-white shadow-xs"
                  : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200"
              }`}
            >
              <Smartphone className="w-3.5 h-3.5" />
              SMS ({smsCount})
            </button>
            <button
              onClick={() => setFilterStatus("completed")}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                filterStatus === "completed"
                  ? "bg-emerald-600 text-white shadow-xs"
                  : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200"
              }`}
            >
              Completed ({completedCount})
            </button>
          </div>

          <div className="relative w-full sm:w-64">
            <Search className="w-3.5 h-3.5 absolute left-3 top-2.5 text-slate-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search contact or task..."
              className="pl-8 text-xs h-8 bg-slate-50 dark:bg-slate-950"
            />
          </div>
        </div>

        {/* Task Cards List */}
        <div className="space-y-3">
          {filtered.length === 0 ? (
            <Card className="p-12 text-center bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
              <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
              <h3 className="font-semibold text-slate-800 dark:text-slate-200 text-sm">All caught up!</h3>
              <p className="text-xs text-slate-400 mt-1">No actions matching your selected criteria.</p>
            </Card>
          ) : (
            filtered.map((action) => (
              <Card
                key={action.id}
                className={`p-4 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 transition-all ${
                  action.status === "completed" ? "opacity-75 bg-slate-50/50" : "hover:border-blue-400 shadow-xs"
                }`}
              >
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                        action.type === "call"
                          ? "bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300"
                          : "bg-purple-100 text-purple-700 dark:bg-purple-950 dark:text-purple-300"
                      }`}
                    >
                      {action.type === "call" ? <Phone className="w-5 h-5" /> : <Smartphone className="w-5 h-5" />}
                    </div>

                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm text-slate-900 dark:text-slate-100">
                          {action.contactName}
                        </span>
                        <Badge
                          variant="outline"
                          className={`text-[10px] px-2 py-0.5 ${
                            action.priority === "High"
                              ? "border-red-300 text-red-600 bg-red-50 dark:bg-red-950/30"
                              : "border-slate-200 text-slate-600"
                          }`}
                        >
                          {action.priority} Priority
                        </Badge>
                        <Badge variant="secondary" className="text-[10px] bg-slate-100 dark:bg-slate-800 text-slate-600">
                          {action.workflow}
                        </Badge>
                      </div>

                      <p className="text-xs font-medium text-slate-700 dark:text-slate-300 mt-1">
                        {action.title}
                      </p>

                      <div className="flex items-center gap-4 text-[11px] text-slate-400 mt-2">
                        <span>📞 {action.phone}</span>
                        {action.email && <span>✉️ {action.email}</span>}
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {action.status === "completed" ? `Completed ${action.completedAt || ""}` : `Due: ${action.dueDate}`}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Actions Column */}
                  <div className="flex items-center gap-2 self-end lg:self-center shrink-0">
                    {action.status === "pending" ? (
                      <>
                        <Button
                          onClick={() => handleStartExecute(action)}
                          size="sm"
                          className="bg-blue-600 hover:bg-blue-700 text-white text-xs gap-1.5 h-8 shadow-xs"
                        >
                          <Play className="w-3 h-3 fill-white" />
                          Execute Action
                        </Button>
                        <Button
                          onClick={() => handleQuickComplete(action.id)}
                          variant="outline"
                          size="sm"
                          className="text-xs h-8 text-emerald-600 border-emerald-200 hover:bg-emerald-50 gap-1"
                        >
                          <Check className="w-3 h-3" />
                          Mark Done
                        </Button>
                      </>
                    ) : (
                      <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300 text-xs gap-1 py-1">
                        <Check className="w-3 h-3" /> Completed
                      </Badge>
                    )}
                    <button
                      onClick={() => handleDeleteAction(action.id)}
                      className="p-1.5 text-slate-400 hover:text-red-500 rounded transition-colors"
                      title="Delete action"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {action.notes && (
                  <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800 text-[11px] text-slate-500 bg-slate-50/50 dark:bg-slate-900/50 p-2 rounded">
                    <span className="font-semibold text-slate-600 dark:text-slate-400">Task Notes: </span>
                    {action.notes}
                  </div>
                )}
              </Card>
            ))
          )}
        </div>
      </div>

      {/* Execution Dialog (Simulated Live Call / Quick SMS) */}
      <Dialog open={!!executingAction} onOpenChange={(open) => !open && setExecutingAction(null)}>
        <DialogContent className="sm:max-w-md bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base text-slate-900 dark:text-slate-100">
              {executingAction?.type === "call" ? (
                <>
                  <PhoneCall className="w-5 h-5 text-sky-600" />
                  Live Outbound Call
                </>
              ) : (
                <>
                  <Smartphone className="w-5 h-5 text-purple-600" />
                  Send Outbound SMS
                </>
              )}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Executing manual action for <strong>{executingAction?.contactName}</strong> ({executingAction?.phone})
            </DialogDescription>
          </DialogHeader>

          {executingAction?.type === "call" ? (
            <div className="space-y-4 py-2">
              <div className="bg-slate-900 text-white rounded-xl p-4 text-center">
                <div className="text-xs text-slate-400">Call Status</div>
                <div className="text-lg font-bold text-emerald-400 mt-1 flex items-center justify-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-ping" />
                  {callConnected ? `Connected (${formatSeconds(callTimer)})` : "Dialing recipient..."}
                </div>
                <div className="text-xs text-slate-300 mt-1 font-mono">
                  {executingAction?.phone} · Colibri HD Voice
                </div>
              </div>

              {executingAction?.script && (
                <div className="bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-xs text-blue-950 dark:text-blue-200">
                  <div className="font-semibold flex items-center gap-1.5 text-blue-700 dark:text-blue-400 mb-1">
                    <Sparkles className="w-3.5 h-3.5" />
                    Recommended Talking Points / Script
                  </div>
                  {executingAction.script}
                </div>
              )}

              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Call Outcome & Log Notes</label>
                <Textarea
                  value={callNotes}
                  onChange={(e) => setCallNotes(e.target.value)}
                  placeholder="Record summary of call, client sentiment, and agreed next steps..."
                  className="mt-1 text-xs min-h-[90px]"
                />
              </div>
            </div>
          ) : (
            <div className="space-y-4 py-2">
              <div className="bg-slate-50 dark:bg-slate-850 p-3 rounded-lg border border-slate-200 dark:border-slate-700 text-xs">
                <div className="text-slate-500">Recipient:</div>
                <div className="font-semibold text-slate-800 dark:text-slate-200">
                  {executingAction?.contactName} — {executingAction?.phone}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">SMS Text Content</label>
                  <span className="text-[10px] text-slate-400">{smsDraft.length} chars (1 SMS segment)</span>
                </div>
                <Textarea
                  value={smsDraft}
                  onChange={(e) => setSmsDraft(e.target.value)}
                  className="mt-1 text-xs min-h-[110px]"
                  placeholder="Type outbound SMS message..."
                />
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" size="sm" onClick={() => setExecutingAction(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleCompleteExecution}
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5"
            >
              <Check className="w-3.5 h-3.5" />
              {executingAction?.type === "call" ? "Hang Up & Complete Task" : "Send SMS & Complete Task"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add New Action Dialog */}
      <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
        <DialogContent className="sm:max-w-lg bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-base text-slate-900 dark:text-slate-100">Add New Manual Action</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Create an operational task assigned to an agent for immediate execution.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreateAction} className="space-y-3.5 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Contact Name *</label>
                <Input
                  required
                  placeholder="e.g. David Miller"
                  value={newAction.contactName}
                  onChange={(e) => setNewAction({ ...newAction, contactName: e.target.value })}
                  className="text-xs mt-1"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Action Type</label>
                <select
                  value={newAction.type}
                  onChange={(e) => setNewAction({ ...newAction, type: e.target.value })}
                  className="w-full mt-1 h-9 rounded-md border border-slate-300 bg-white px-3 py-1 text-xs dark:bg-slate-950 dark:border-slate-800"
                >
                  <option value="call">Phone Call</option>
                  <option value="sms">Outbound SMS</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Phone Number</label>
                <Input
                  placeholder="(555) 000-0000"
                  value={newAction.phone}
                  onChange={(e) => setNewAction({ ...newAction, phone: e.target.value })}
                  className="text-xs mt-1"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Priority</label>
                <select
                  value={newAction.priority}
                  onChange={(e) => setNewAction({ ...newAction, priority: e.target.value })}
                  className="w-full mt-1 h-9 rounded-md border border-slate-300 bg-white px-3 py-1 text-xs dark:bg-slate-950 dark:border-slate-800"
                >
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                  <option value="Normal">Normal</option>
                </select>
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Task Title *</label>
              <Input
                required
                placeholder="e.g. Confirm discovery meeting & discuss package options"
                value={newAction.title}
                onChange={(e) => setNewAction({ ...newAction, title: e.target.value })}
                className="text-xs mt-1"
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Script / Message Template</label>
              <Textarea
                placeholder="Talking points for call or pre-drafted message..."
                value={newAction.script}
                onChange={(e) => setNewAction({ ...newAction, script: e.target.value })}
                className="text-xs mt-1 min-h-[70px]"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" size="sm" onClick={() => setIsAddOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" size="sm" className="bg-blue-600 hover:bg-blue-700 text-white">
                Save & Add to Queue
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

